/**
 * SegmentDetailsDrawer — right-side panel showing full details of the
 * currently selected movement segment.
 *
 * Shows: segment ID, time bounds, type, source, completeness, confidence,
 * evidence refs (with copy-to-clipboard), geometry coords, and meta info.
 */

import { useState } from 'react';
import { X, Copy, Check, MapPin, Clock, Shield, FileText, Package, User } from 'lucide-react';
import type { MovementSegment, TimelineMeta } from '../../../types/vehicleMovementTimeline';

interface SegmentDetailsDrawerProps {
  segment: MovementSegment;
  meta: TimelineMeta;
  asOf?: string;
  onClose: () => void;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatISO(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
}

function formatDuration(fromIso: string, toIso: string): string {
  const ms = Date.parse(toIso) - Date.parse(fromIso);
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`;
  const h = Math.floor(ms / 3_600_000);
  const m = Math.round((ms % 3_600_000) / 60_000);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // fallback: select text
    }
  };
  return (
    <button
      onClick={handleCopy}
      className="p-1 rounded hover:bg-accent transition-colors"
      title="Copy to clipboard"
    >
      {copied ? (
        <Check className="w-3 h-3 text-success" />
      ) : (
        <Copy className="w-3 h-3 text-muted-foreground" />
      )}
    </button>
  );
}

// ── Detail row ──────────────────────────────────────────────────────────────

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 py-1.5">
      <span className="text-xs text-muted-foreground w-24 shrink-0">{label}</span>
      <span className="text-xs text-foreground break-all">{value}</span>
    </div>
  );
}

// ── Component ───────────────────────────────────────────────────────────────

export default function SegmentDetailsDrawer({
  segment,
  meta,
  asOf,
  onClose,
}: SegmentDetailsDrawerProps) {
  const seg = segment;

  // Type badge colours
  const typeBadge: Record<string, string> = {
    MOVING: 'bg-success/20 text-success',
    STOPPED: 'bg-warning/20 text-warning',
    UNKNOWN: 'bg-secondary text-foreground',
  };

  // Source badges are the provenance tiers (Constitution §13.2):
  // observed = green, declared = blue, inferred = violet.
  const sourceBadge: Record<string, string> = {
    OBSERVED_TELEMETRY: 'bg-provenance-observed/20 text-provenance-observed',
    DECLARED_LEG: 'bg-provenance-declared/20 text-provenance-declared',
    INFERRED_LEG: 'bg-provenance-inferred/20 text-provenance-inferred',
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden flex flex-col max-h-screen-80">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card/80">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <FileText className="w-4 h-4 text-muted-foreground" />
          Segment Details
        </h3>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-accent transition-colors"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      {/* Body (scrollable) */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {/* Badges */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${typeBadge[seg.type] ?? 'bg-secondary text-foreground'}`}>
            {seg.type}
          </span>
          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${sourceBadge[seg.source] ?? 'bg-secondary text-foreground'}`}>
            {seg.source?.replace(/_/g, ' ')}
          </span>
          {seg.completeness && (
            <span className="px-2 py-0.5 rounded text-xs font-semibold bg-secondary text-foreground">
              {seg.completeness}
            </span>
          )}
        </div>

        {/* Core details */}
        <div className="divide-y divide-border/50">
          <DetailRow
            label="Segment ID"
            value={
              <span className="flex items-center gap-1 font-mono text-xs">
                {seg.segment_id?.slice(0, 16)}…
                <CopyButton text={seg.segment_id} />
              </span>
            }
          />

          <DetailRow
            label="Start"
            value={
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3 text-muted-foreground" />
                {formatISO(seg.start_ts)}
              </span>
            }
          />

          <DetailRow
            label="End"
            value={
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3 text-muted-foreground" />
                {formatISO(seg.end_ts)}
              </span>
            }
          />

          <DetailRow
            label="Duration"
            value={formatDuration(seg.start_ts, seg.end_ts)}
          />

          {seg.confidence !== undefined && seg.confidence !== null && (
            <DetailRow
              label="Confidence"
              value={
                <span className="flex items-center gap-1">
                  <Shield className="w-3 h-3 text-muted-foreground" />
                  {typeof seg.confidence === 'number'
                    ? `${(seg.confidence * 100).toFixed(0)}%`
                    : String(seg.confidence)}
                </span>
              }
            />
          )}
        </div>

        {/* Geometry */}
        {seg.geometry && (seg.geometry.start_point || seg.geometry.end_point) && (
          <div>
            <h4 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Geometry
            </h4>
            <div className="bg-background rounded p-2 text-xs font-mono text-foreground space-y-1">
              {seg.geometry.start_point && seg.geometry.start_point.lat !== undefined && seg.geometry.start_point.lon !== undefined && (
                <div>Start: {seg.geometry.start_point.lat.toFixed(5)}, {seg.geometry.start_point.lon.toFixed(5)}</div>
              )}
              {seg.geometry.end_point && seg.geometry.end_point.lat !== undefined && seg.geometry.end_point.lon !== undefined && (
                <div>End: {seg.geometry.end_point.lat.toFixed(5)}, {seg.geometry.end_point.lon.toFixed(5)}</div>
              )}
              {seg.geometry.bbox && 
                seg.geometry.bbox.minLat !== undefined && 
                seg.geometry.bbox.minLon !== undefined && 
                seg.geometry.bbox.maxLat !== undefined && 
                seg.geometry.bbox.maxLon !== undefined && (
                <div className="text-muted-foreground">
                  BBox: [{seg.geometry.bbox.minLat.toFixed(4)}, {seg.geometry.bbox.minLon.toFixed(4)}] → [{seg.geometry.bbox.maxLat.toFixed(4)}, {seg.geometry.bbox.maxLon.toFixed(4)}]
                </div>
              )}
            </div>
          </div>
        )}

        {/* Orders (v0.2 — Order POV enrichment) */}
        {seg.associated && seg.associated.order_entity_ids && seg.associated.order_entity_ids.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
              <Package className="w-3 h-3" />
              Orders ({seg.associated.order_entity_ids.length})
            </h4>
            <div className="bg-background rounded max-h-48 overflow-y-auto">
              <ul className="divide-y divide-card">
                {seg.associated.order_entity_ids.map((orderId) => {
                  const ref = seg.associated?.order_refs?.find(
                    (r) => r.order_entity_id === orderId,
                  );
                  return (
                    <li
                      key={orderId}
                      className="flex items-center justify-between px-2 py-1.5 hover:bg-card transition-colors"
                    >
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-mono text-foreground truncate">
                          {orderId}
                        </span>
                        {ref?.source_order_key && (
                          <span className="text-xs text-muted-foreground">
                            Source: {ref.source_order_key}
                          </span>
                        )}
                        {ref && (
                          <span className="text-xs text-muted-foreground">
                            {ref.derivation.replace(/_/g, ' ')} · {(ref.confidence * 100).toFixed(0)}%
                          </span>
                        )}
                      </div>
                      <CopyButton text={orderId} />
                    </li>
                  );
                })}
              </ul>
            </div>
            {/* Copy all order IDs */}
            <div className="mt-1 flex justify-end">
              <CopyButton text={seg.associated.order_entity_ids.join(', ')} />
            </div>
          </div>
        )}

        {/* Drivers (v0.3 — Driver POV enrichment) */}
        {seg.associated && seg.associated.driver_entity_ids && seg.associated.driver_entity_ids.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
              <User className="w-3 h-3" />
              Drivers ({seg.associated.driver_entity_ids.length})
            </h4>
            <div className="bg-background rounded max-h-48 overflow-y-auto">
              <ul className="divide-y divide-card">
                {seg.associated.driver_entity_ids.map((driverId) => {
                  const ref = seg.associated?.driver_refs?.find(
                    (r) => r.driver_entity_id === driverId,
                  );
                  return (
                    <li
                      key={driverId}
                      className="flex items-center justify-between px-2 py-1.5 hover:bg-card transition-colors"
                    >
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-mono text-foreground truncate">
                          {driverId}
                        </span>
                        {ref?.source_driver_key && (
                          <span className="text-xs text-muted-foreground">
                            Source: {ref.source_driver_key}
                          </span>
                        )}
                        {ref && (
                          <span className="text-xs text-muted-foreground">
                            {ref.derivation.replace(/_/g, ' ')} · {(ref.confidence * 100).toFixed(0)}%
                          </span>
                        )}
                      </div>
                      <CopyButton text={driverId} />
                    </li>
                  );
                })}
              </ul>
            </div>
            {/* Copy all driver IDs */}
            <div className="mt-1 flex justify-end">
              <CopyButton text={seg.associated.driver_entity_ids.join(', ')} />
            </div>
          </div>
        )}

        {/* Evidence refs */}
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-2">
            Evidence ({seg.evidence_refs?.length ?? 0} events)
          </h4>
          <div className="bg-background rounded max-h-40 overflow-y-auto">
            {seg.evidence_refs?.length > 0 ? (
              <ul className="divide-y divide-card">
                {seg.evidence_refs.map((ref, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between px-2 py-1.5 hover:bg-card transition-colors"
                  >
                    <span className="text-xs font-mono text-foreground truncate">
                      {ref}
                    </span>
                    <CopyButton text={ref} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground p-2">No evidence events</p>
            )}
          </div>
        </div>

        {/* Notes */}
        {seg.notes && (
          <div>
            <h4 className="text-xs font-medium text-muted-foreground mb-1">Notes</h4>
            <p className="text-xs text-foreground bg-background rounded p-2">{seg.notes}</p>
          </div>
        )}

        {/* Meta */}
        <div className="border-t border-border pt-3">
          <h4 className="text-xs font-medium text-muted-foreground mb-2">Projection Meta</h4>
          <div className="text-xs text-muted-foreground space-y-1">
            <div>Version: <span className="text-foreground">{meta.projection_version ?? '—'}</span></div>
            <div>Computed at: <span className="text-foreground">{meta.computed_at ? formatISO(meta.computed_at) : '—'}</span></div>
            {asOf && <div>asOf: <span className="text-foreground">{formatISO(asOf)}</span></div>}
          </div>
        </div>
      </div>
    </div>
  );
}
